/**
 * Thin fetch wrapper for the KSENA Stavby API.
 *
 * - Reads token from `useAuthStore.getState().token`
 * - Auto-attaches `Authorization: Bearer` header
 * - Handles 401 → refresh token flow (one retry)
 * - Base URL from `VITE_API_URL` env or "" (same-origin via Vite proxy / reverse proxy)
 */

import type { ApiError, User } from "@/types";
import { assertCurrentSession, getSession, SessionChangedError } from "./sessionLifecycle";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

/**
 * The auth store is imported lazily to avoid a circular import
 * (api.ts ← authStore.ts ← api.ts).
 */
let _getAuthState: (() => {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  refreshAuth: () => Promise<boolean>;
  logout: () => void;
}) | null = null;

async function getAuthState() {
  if (!_getAuthState) {
    const mod = await import("@/stores/authStore");
    _getAuthState = () => mod.useAuthStore.getState();
  }
  return _getAuthState();
}

async function refreshAccessToken(session: ReturnType<typeof getSession>, failedToken: string | null): Promise<string | null> {
  const auth = await getAuthState();
  assertCurrentSession(session);
  // A concurrent request may already have refreshed this access token.
  if (auth.token && auth.token !== failedToken) return auth.token;
  const refreshed = await auth.refreshAuth();
  assertCurrentSession(session);
  return refreshed ? (await getAuthState()).token : null;
}

export class ApiRequestError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown, code?: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code ?? `HTTP_${status}`;
    this.details = details;
  }
}

async function request<T>(
  url: string,
  options: RequestInit = {},
  isRetry = false,
  session = getSession(),
): Promise<T> {
  const auth = await getAuthState();
  assertCurrentSession(session);

  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  if (auth.token) {
    headers.set("Authorization", `Bearer ${auth.token}`);
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${url}`, {
      ...options,
      headers,
      credentials: "include",
      signal: session.controller.signal,
    });
  } catch (error) {
    if (getSession() !== session) throw new SessionChangedError();
    throw error;
  }
  assertCurrentSession(session);

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  let data: unknown;
  const contentType = response.headers.get("Content-Type");
  if (contentType?.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }
  assertCurrentSession(session);

  if (response.ok) {
    return data as T;
  }

  // 401 — attempt a single token refresh, then retry
  if (response.status === 401 && !isRetry) {
    const accessToken = await refreshAccessToken(session, auth.token);
    if (accessToken) {
      return request<T>(url, options, true, session);
    }
  }

  const apiError = data as ApiError | undefined;
  throw new ApiRequestError(
    apiError?.error ?? `Požadavek selhal (${response.status})`,
    response.status,
    apiError?.details,
    apiError?.code,
  );
}

/** Build a query string, skipping empty values. */
export function qs(params: object = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : "";
}

export function get<T>(url: string): Promise<T> {
  return request<T>(url, { method: "GET" });
}

export function post<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function put<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: "PUT",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function patch<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: "PATCH",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function del<T = void>(url: string): Promise<T> {
  return request<T>(url, { method: "DELETE" });
}

const api = { get, post, put, patch, del };
export default api;
